import * as React from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'
import { FieldLabelContent } from './field-label'

/**
 * JPG Academy Select / Combobox
 *
 * Dropdown selection component matching Input styling.
 * Gold focus ring, warm borders, Garamond labels, error states.
 *
 * Motion:
 *   - Dropdown slides down with opacity fade
 *   - Options stagger in
 *   - Chevron rotates smoothly
 *   - Search input animated open
 */

export interface SelectOption {
	value: string
	label: string
	/** Optional group label for optgroups */
	group?: string
	/** Disable this option */
	disabled?: boolean
}

export interface SelectProps {
	/** Label text */
	label?: string
	/** Contextual help shown beside the label */
	labelHelp?: React.ReactNode
	/** Optional title for the label help tooltip */
	labelHelpTitle?: string
	/** Helper text */
	hint?: string
	/** Error message */
	error?: string
	/** Available options */
	options: SelectOption[]
	/** Selected value */
	value?: string
	/** Change handler */
	onChange?: (value: string) => void
	/** Placeholder text */
	placeholder?: string
	/** Enable type-to-filter search */
	searchable?: boolean
	/** Disable the select */
	disabled?: boolean
	/** Input size */
	size?: 'sm' | 'md' | 'lg'
	/** Additional class */
	className?: string
	/** ID override */
	id?: string
	/** Dropdown direction */
	dropdownDirection?: 'down' | 'up'
}

const sizeStyles = {
	sm: 'h-8 text-xs px-2.5',
	md: 'h-10 text-sm px-3',
	lg: 'h-12 text-base px-4',
}

const triggerSizeStyles = {
	sm: 'h-8 text-xs px-2.5',
	md: 'h-10 text-sm px-3',
	lg: 'h-12 text-base px-4',
}

function HighlightedText({ text, query }: { text: string; query: string }) {
	if (!query) return <>{text}</>
	const idx = text.toLowerCase().indexOf(query.toLowerCase())
	if (idx === -1) return <>{text}</>

	return (
		<>
			{text.slice(0, idx)}
			<span className="font-semibold bg-gold/20 rounded px-0.5">{text.slice(idx, idx + query.length)}</span>
			{text.slice(idx + query.length)}
		</>
	)
}

export function Select({
	label,
	labelHelp,
	labelHelpTitle,
	hint,
	error,
	options,
	value,
	onChange,
	placeholder = 'Select...',
	searchable = false,
	disabled = false,
	size = 'md',
	className,
	id,
	dropdownDirection = 'down',
}: SelectProps) {
	const selectId = id || label?.toLowerCase().replace(/\s+/g, '-')
	const [isOpen, setIsOpen] = React.useState(false)
	const [search, setSearch] = React.useState('')
	const containerRef = React.useRef<HTMLDivElement>(null)
	const triggerRef = React.useRef<HTMLButtonElement>(null)
	const dropdownRef = React.useRef<HTMLDivElement>(null)
	const searchInputRef = React.useRef<HTMLInputElement>(null)
	const listboxId = `${selectId}-listbox`

	// Position the portal dropdown relative to the trigger
	const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>({})

	React.useLayoutEffect(() => {
		if (!isOpen || !triggerRef.current) return
		const rect = triggerRef.current.getBoundingClientRect()
		const dropdownHeight = 240 // max-h-60 = 15rem = 240px
		const spaceBelow = window.innerHeight - rect.bottom
		const spaceAbove = rect.top
		const dropUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

		setDropdownStyle({
			position: 'fixed',
			top: dropUp ? rect.top - Math.min(dropdownHeight, spaceAbove - 8) : rect.bottom + 4,
			left: rect.left,
			width: rect.width,
			zIndex: 9999,
		})
	}, [isOpen])

	// Close on outside click (accounts for portaled dropdown)
	React.useEffect(() => {
		function handleClick(e: MouseEvent) {
			const target = e.target as Node
			const clickedInside =
				(containerRef.current && containerRef.current.contains(target)) ||
				(dropdownRef.current && dropdownRef.current.contains(target))
			if (!clickedInside) {
				setIsOpen(false)
				setSearch('')
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [])

	// Focus search input when opened
	React.useEffect(() => {
		if (isOpen && searchable && searchInputRef.current) {
			searchInputRef.current.focus()
		}
	}, [isOpen, searchable])

	// Close on Escape
	React.useEffect(() => {
		function handleKey(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				setIsOpen(false)
				setSearch('')
			}
		}
		if (isOpen) {
			document.addEventListener('keydown', handleKey)
			return () => document.removeEventListener('keydown', handleKey)
		}
	}, [isOpen])

	const selectedOption = options.find(o => o.value === value)

	const filteredOptions = searchable && search
		? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
		: options

	// Group options
	const groups = React.useMemo(() => {
		const grouped = new Map<string, SelectOption[]>()
		for (const opt of filteredOptions) {
			const key = opt.group || ''
			if (!grouped.has(key)) grouped.set(key, [])
			grouped.get(key)!.push(opt)
		}
		return grouped
	}, [filteredOptions])

	const handleSelect = (opt: SelectOption) => {
		if (opt.disabled) return
		onChange?.(opt.value)
		setIsOpen(false)
		setSearch('')
	}

	// Keyboard navigation within dropdown
	const [activeIndex, setActiveIndex] = React.useState(-1)
	const optionElements = React.useRef<(HTMLLIElement | null)[]>([])

	const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
			e.preventDefault()
			setIsOpen(true)
			setActiveIndex(0)
		}
		if (e.key === 'Tab') {
			setIsOpen(false)
		}
	}

	// Scroll active item into view on keyboard nav
	React.useEffect(() => {
		const el = optionElements.current[activeIndex]
		if (el) el.scrollIntoView({ block: 'nearest' })
	}, [activeIndex])

	const handleListKeyDown = (e: React.KeyboardEvent) => {
		const flatOptions = filteredOptions.filter(o => !o.disabled)
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setActiveIndex(prev => Math.min(prev + 1, flatOptions.length - 1))
		}
		if (e.key === 'ArrowUp') {
			e.preventDefault()
			setActiveIndex(prev => Math.max(prev - 1, 0))
		}
		if (e.key === 'Enter') {
			e.preventDefault()
			if (flatOptions[activeIndex]) {
				handleSelect(flatOptions[activeIndex])
			}
		}
	}

	return (
		<div className="space-y-1.5" ref={containerRef}>
			{label && (
				<label
					htmlFor={selectId}
					className="block text-sm font-medium text-foreground font-ui"
				>
					<FieldLabelContent label={label} labelHelp={labelHelp} labelHelpTitle={labelHelpTitle} />
				</label>
			)}

			<div className="relative">
				{/* Trigger */}
				<button
					ref={triggerRef}
					id={selectId}
					type="button"
					role="combobox"
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-controls={isOpen ? listboxId : undefined}
					aria-labelledby={label ? selectId : undefined}
					disabled={disabled}
					onClick={() => {
						if (disabled) return
						setIsOpen(prev => !prev)
						if (isOpen) setSearch('')
					}}
					onKeyDown={handleTriggerKeyDown}
					className={cn(
						'w-full font-ui rounded-md border transition-all duration-fast ease-default',
						'bg-background text-foreground text-left',
						'disabled:cursor-not-allowed disabled:opacity-50',
						'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
						triggerSizeStyles[size],
						'flex items-center justify-between gap-2',
						'pr-8',
						error
							? 'border-error/60 focus:ring-error/30 focus:border-error'
							: isOpen
								? 'border-gold'
								: 'border-input hover:border-navy-300',
						className,
					)}
				>
					<span className={cn('truncate', !selectedOption && 'text-muted-foreground')}>
						{selectedOption ? selectedOption.label : placeholder}
					</span>
					<motion.span
						className={cn(
							'absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground',
						)}
						animate={{ rotate: isOpen ? 180 : 0 }}
						transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
						aria-hidden="true"
					>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="m6 9 6 6 6-6" />
						</svg>
					</motion.span>
				</button>

				{/* Dropdown — portaled to body to escape overflow containers */}
				{isOpen && createPortal(
					<AnimatePresence>
						<motion.div
							ref={dropdownRef}
							className={cn(
								'bg-card border border-border rounded-lg',
								'shadow-lg',
								'overflow-hidden',
								'max-h-60 overflow-y-auto scrollbar-warm',
							)}
							role="listbox"
							id={listboxId}
							onKeyDown={handleListKeyDown}
							style={dropdownStyle}
							initial={{ opacity: 0, y: -4, scale: 0.98 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							exit={{ opacity: 0, y: -4, scale: 0.98 }}
							transition={{ duration: 0.15, ease: [0, 0, 0.2, 1] }}
						>
							{/* Search input */}
							{searchable && (
								<motion.div
									className="p-2 border-b border-border sticky top-0 bg-card z-10"
									initial={{ height: 0, opacity: 0 }}
									animate={{ height: 'auto', opacity: 1 }}
									transition={{ duration: 0.15 }}
								>
									<input
										ref={searchInputRef}
										type="text"
										className={cn(
											'w-full font-ui text-sm rounded-md border border-input px-2.5 py-1.5',
											'bg-background text-foreground placeholder:text-muted-foreground',
											'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
										)}
										placeholder="Type to search..."
										value={search}
										onChange={e => {
											setSearch(e.target.value)
											setActiveIndex(0)
										}}
										onClick={e => e.stopPropagation()}
									/>
									{filteredOptions.length === 0 && (
										<p className="text-xs text-muted-foreground text-center py-2 font-ui">
											No results found
										</p>
									)}
								</motion.div>
							)}

							{/* Options */}
							<ul role="presentation">
								{Array.from(groups.entries()).map(([group, opts]) => (
									<React.Fragment key={group}>
										{group && (
											<li className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider font-ui bg-muted/50">
												{group}
											</li>
										)}
										{opts.map((opt, optIdx) => {
											const flatIndex = filteredOptions
												.filter(o => !o.disabled)
												.indexOf(opt)
											return (
												<motion.li
													key={opt.value}
													ref={(el: HTMLLIElement | null) => { optionElements.current[flatIndex] = el }}
													role="option"
													aria-selected={opt.value === value}
													aria-disabled={opt.disabled || undefined}
													onClick={() => handleSelect(opt)}
													onMouseEnter={() => setActiveIndex(flatIndex)}
													className={cn(
														'px-3 py-2 text-sm font-ui cursor-pointer transition-colors duration-fast',
														'select-none',
														opt.value === value
															? 'bg-navy/10 text-navy-900 font-medium'
															: 'text-foreground hover:bg-cream/60',
														opt.disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
														flatIndex === activeIndex && !opt.disabled && 'bg-cream/80',
													)}
													initial={{ opacity: 0, x: -4 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{
														duration: 0.15,
														delay: optIdx * 0.03,
														ease: [0.4, 0, 0.2, 1],
													}}
												>
													{searchable ? (
														<HighlightedText text={opt.label} query={search} />
													) : (
														opt.label
													)}
												</motion.li>
											)
										})}
									</React.Fragment>
								))}
							</ul>
						</motion.div>
					</AnimatePresence>,
					document.body,
				)}
			</div>

			{error && (
				<motion.p
					id={`${selectId}-error`}
					className="text-xs text-error font-ui"
					initial={{ opacity: 0, y: -4 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.2 }}
				>
					{error}
				</motion.p>
			)}
			{hint && !error && (
				<p id={`${selectId}-hint`} className="text-xs text-muted-foreground font-ui">
					{hint}
				</p>
			)}
		</div>
	)
}
